import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { branchesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminBranches = () => {
  const [branches, setBranches] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    halls_count: '',
    facilities: '',
    image: '',
    map_url: '',
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const response = await branchesAPI.getAll();
      console.log('Loaded branches:', response.data);
      console.log('First branch ID:', response.data[0]?._id);
      console.log('First branch:', response.data[0]);
      setBranches(response.data);
    } catch (error) {
      toast({ title: 'Hata', description: 'Şubeler yüklenemedi', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        halls_count: parseInt(formData.halls_count),
        facilities: formData.facilities.split(',').map(f => f.trim()).filter(f => f),
      };

      console.log('Submitting data:', data);
      console.log('Editing branch ID:', editingBranch?._id);

      if (editingBranch) {
        const response = await branchesAPI.update(editingBranch._id, data);
        console.log('Update response:', response);
        toast({ title: 'Başarılı', description: 'Şube güncellendi' });
      } else {
        const response = await branchesAPI.create(data);
        console.log('Create response:', response);
        toast({ title: 'Başarılı', description: 'Şube eklendi' });
      }

      setIsDialogOpen(false);
      resetForm();
      loadBranches();
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error response:', error.response?.data);
      toast({ 
        title: 'Hata', 
        description: error.response?.data?.detail || 'Şube kaydedilemedi', 
        variant: 'destructive' 
      });
    }
  };

  const handleEdit = (branch) => {
    console.log('Editing branch:', branch);
    setEditingBranch(branch);
    
    // Facilities array kontrolü
    const facilitiesString = Array.isArray(branch.facilities) 
      ? branch.facilities.join(', ') 
      : (branch.facilities || '');
    
    setFormData({
      name: branch.name || '',
      slug: branch.slug || '',
      city: branch.city || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      halls_count: branch.halls_count ? branch.halls_count.toString() : '',
      facilities: facilitiesString,
      image: branch.image || '',
      map_url: branch.map_url || '',
      is_active: branch.is_active !== undefined ? branch.is_active : true,
    });
    
    console.log('Form data set:', {
      name: branch.name,
      city: branch.city,
      facilities: facilitiesString,
    });
    
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu şubeyi silmek istediğinizden emin misiniz?')) {
      try {
        await branchesAPI.delete(id);
        toast({ title: 'Başarılı', description: 'Şube silindi' });
        loadBranches();
      } catch (error) {
        toast({ title: 'Hata', description: 'Şube silinemedi', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setEditingBranch(null);
    setFormData({
      name: '',
      slug: '',
      city: '',
      address: '',
      phone: '',
      email: '',
      halls_count: '',
      facilities: '',
      image: '',
      map_url: '',
      is_active: true,
    });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Şube Yönetimi</h1>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
          >
            <Plus size={20} className="mr-2" />
            Yeni Şube
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((branch) => (
            <Card key={branch._id} className="bg-gray-900 border-gray-800 p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-white font-bold text-xl">{branch.name}</h3>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  branch.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {branch.is_active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              <div className="text-gray-300 space-y-2 mb-4">
                <p><span className="text-gray-500">Şehir:</span> {branch.city}</p>
                <p><span className="text-gray-500">Adres:</span> {branch.address}</p>
                <p><span className="text-gray-500">Telefon:</span> {branch.phone}</p>
                {branch.email && <p><span className="text-gray-500">E-posta:</span> {branch.email}</p>}
                <p><span className="text-gray-500">Salon Sayısı:</span> {branch.halls_count}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(branch)}
                  className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  <Edit size={16} className="mr-1" />
                  Düzenle
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(branch._id)}
                  className="border-red-700 text-red-500 hover:bg-red-900/20"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingBranch ? 'Şube Düzenle' : 'Yeni Şube Ekle'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Şube Adı</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: Cinetime AVM"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Şehir</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    required
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="0xxx xxx xx xx"
                    required
                  />
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="ornek@cinefora.com.tr"
                  />
                </div>
              </div>

              <div>
                <Label>Adres</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>

              <div>
                <Label>Salon Sayısı</Label>
                <Input
                  type="number"
                  value={formData.halls_count}
                  onChange={(e) => setFormData({ ...formData, halls_count: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>

              <div>
                <Label>Slug (URL için)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: kecioren-cinefora (boşluksuz, küçük harf)"
                />
                <p className="text-gray-500 text-xs mt-1">URL'de görünecek: /salonlarimiz/kecioren-cinefora</p>
              </div>

              <div>
                <Label>Salon Görseli URL</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="https://example.com/salon-gorsel.jpg"
                />
                <p className="text-gray-500 text-xs mt-1">Salon içi veya dış görünüm fotoğrafı</p>
              </div>

              <div>
                <Label>Harita Linki (Google Maps)</Label>
                <Input
                  value={formData.map_url}
                  onChange={(e) => setFormData({ ...formData, map_url: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="https://maps.google.com/..."
                />
                <p className="text-gray-500 text-xs mt-1">Google Maps'ten "Paylaş" → "Link Kopyala"</p>
              </div>

              <div>
                <Label>Özellikler (virgülle ayırın)</Label>
                <Input
                  value={formData.facilities}
                  onChange={(e) => setFormData({ ...formData, facilities: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: Dolby Atmos, 4K, IMAX"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Şube Aktif</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold">
                  {editingBranch ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBranches;
